import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import TriEventsLoadingScreen from '../../components/common/TriEventsLoadingScreen';
import BackButton from '../../components/common/BackButton';
import ScrollableDescription from '../../components/common/ScrollableDescription';

// Shadcn Components
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Card, CardContent, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Input } from "../../components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";

// Icons
import { Calendar, Clock, MapPin, Mail, Phone, Users, Ticket, Edit, Trash2, ExternalLink, Bell } from 'lucide-react';

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useEvent } from '../../hooks/event/useEvent';
import { WaiverItemType } from '@/types/event/WaiverItem';
import { cn } from "@/lib/utils";

function formatDate(dateStr: string): string {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[month - 1]} ${day}, ${year}`;
}

const ViewEvent: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const { getTenantPath } = useTenantNavigation();
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    
    const {
        event,
        loading,
        error,
        isEditing,
        showDelete,
        message,
        formData,
        prices,
        ticketsAvailable,
        ticketsAvailableByPrice,
        originalTotalTickets,
        errors,
        setMessage,
        setFormData,
        handleEdit,
        handleCancel,
        handleUpdate,
        handleDelete,
        handleChange,
        handlePriceTierChange,
        addPrice,
        deletePrice,
        handleBursaryOptionChange,
        addBursaryOption,
        removeBursaryOption,
        addWaiverItem,
        removeWaiverItem,
        handleWaiverItemChange
    } = useEvent(uuid!);

    const handleCheckout = () => {
        if (!event) return;
        navigate(`/checkout/${event.uuid}`);
    };

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!event) return;
        setIsDeleting(true);
        try {
            await handleDelete();
            setShowDeleteDialog(false);
            navigate(getTenantPath('/dashboard'));
        } catch (error) {
            setMessage(error instanceof Error ? error.message : 'Failed to delete event');
            setShowDeleteDialog(false);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return <TriEventsLoadingScreen loading={loading} />;
    }

    if (error) {
        return (
            <section className="min-h-screen bg-background">
                <div className="container mx-auto px-4 py-8">
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                </div>
            </section>
        );
    }

    if (!event) {
        return (
            <section className="min-h-screen bg-background">
                <div className="container mx-auto px-4 py-8">
                    <Alert>
                        <AlertDescription>Event not found</AlertDescription>
                    </Alert>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-background">
            <div className="container mx-auto pb-12">
                <div>
                    <div className="mb-0 md:mb-6">
                        <BackButton to={getTenantPath('/dashboard')} text="Back to Dashboard" />
                    </div>

                    {/* ////////////////////////////////////////////////////////////////////////////////////////////////////////////// */}
                    {/* TEMP: Custom Orientation Checkout Links */}
                    <div className="mb-8 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded shadow flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <span className="font-semibold text-yellow-700 mr-4">Custom Orientation Checkout (TEMP):</span>
                        <div className="flex flex-wrap gap-2">
                            {/* <a href={`/orientation/computing/${event?.uuid}`} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">Computing Orientation</a> */}
                            <a href={`/orientation/computing`} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">Computing Orientation</a>
                            {/* <a href={`/orientation/nursing/${event?.uuid}`} className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition">Nursing Orientation</a> */}
                            <a href={`/orientation/nursing`} className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition">Nursing Orientation</a>
                            {/* <a href={`/orientation/healthsciences/${event?.uuid}`} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition">Health Sci Orientation</a> */}
                            <a href={`/orientation/healthsciences`} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition">Health Sci Orientation</a>
                            {/* <a href={`/orientation/commerce/${event?.uuid}`} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition">Commerce Orientation</a> */}
                            <a href={`/orientation/commerce`} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition">Commerce Orientation</a>
                            {/* <a href={`/asterisk/${event?.uuid}`} className="px-4 py-2 bg-black text-white rounded hover:bg-gray-700 transition">Asterisk</a> */}
                            <a href={`/asterisk`} className="px-4 py-2 bg-black text-white rounded hover:bg-gray-700 transition">Asterisk</a>
                        </div>
                    </div>
                    {/* ////////////////////////////////////////////////////////////////////////////////////////////////////////////// */}

                    <Card className="border-none bg-[#23d6e4] text-white">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-3xl font-bold mb-2 text-white">
                                        {event.title}
                                    </CardTitle>
                                    <CardDescription className="text-white/80">
                                        Event Details
                                    </CardDescription>
                                </div>
                                    {!isEditing && (
                                    <Button
                                            onClick={handleEdit}
                                        size="lg"
                                        className="w-full md:w-auto gap-3 text-lg transition-transform duration-200 hover:scale-105"
                                        >
                                        <Edit className="h-6 w-6 stroke-[3.5]" />
                                            Edit Event
                                    </Button>
                                    )}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="mt-6 space-y-6">
                            {isEditing ? (
                            <Card className="mt-6">
                                <CardContent className="p-6 space-y-6">
                                <div className="space-y-4">
                                    <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Title</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                        />
                                        {errors.title && (
                                            <p className="text-sm text-destructive mt-1">{errors.title}</p>
                                        )}
                                    </div>
                                    <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black"
                                            rows={4}
                                            maxLength={5000}
                                        />
                                        <div className="flex justify-between items-center mt-1">
                                            {errors.description && (
                                                <p className="text-destructive text-sm">{errors.description}</p>
                                            )}
                                            <p className="text-muted-foreground text-sm ml-auto">
                                                {5000 - formData.description.length} characters remaining
                                            </p>
                                        </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                        />
                                        {errors.date && (
                                            <p className="text-sm text-destructive mt-1">{errors.date}</p>
                                        )}
                                    </div>
                                    <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Location</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                        />
                                        {errors.location && (
                                            <p className="text-sm text-destructive mt-1">{errors.location}</p>
                                        )}
                                    </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Start Time</label>
                                            <input
                                                type="time"
                                                name="time_start"
                                                value={formData.time_start}
                                                onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                            />
                                            {errors.time_start && (
                                                <p className="text-sm text-destructive mt-1">{errors.time_start}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">End Time</label>
                                            <input
                                                type="time"
                                                name="time_end"
                                                value={formData.time_end}
                                                onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12 [-webkit-appearance:none] [appearance:none]"
                                            />
                                            {errors.time_end && (
                                                <p className="text-sm text-destructive mt-1">{errors.time_end}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Contact Email</label>
                                            <input
                                                type="email"
                                                name="contact_email"
                                                value={formData.contact_email}
                                                onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                            />
                                            {errors.contact_email && (
                                                <p className="text-sm text-destructive mt-1">{errors.contact_email}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-2">Contact Number</label>
                                            <input
                                                type="tel"
                                                name="contact_number"
                                                value={formData.contact_number}
                                                onChange={handleChange}
                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                            />
                                            {errors.contact_number && (
                                                <p className="text-sm text-destructive mt-1">{errors.contact_number}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-xl font-semibold">Ticket Tiers</h3>
                                            <Button 
                                                onClick={addPrice} 
                                                variant="outline"
                                                className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                            >
                                                Add Tier
                                            </Button>
                                    </div>
                                    {prices && prices.map((price, index) => (
                                            <Card key={price.uuid ? price.uuid : index} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                <CardContent className="p-4">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="text-lg font-semibold">Tier {index + 1}</h4>
                                                {prices.length > 1 && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                        onClick={() => deletePrice(index)}
                                                                className="text-destructive hover:text-destructive/90"
                                                    >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                )}
                                            </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                        Tier Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={price.nickname}
                                                        onChange={(e) => handlePriceTierChange(index, 'nickname', e.target.value)}
                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                    />
                                                    {errors[`tier_${index}_name`] && (
                                                        <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_name`]}</p>
                                                    )}
                                                </div>
                                                <div>
                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                        Price (CAD)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={isNaN(price.dollar_cost) ? '' : price.dollar_cost}
                                                        onChange={(e) => handlePriceTierChange(index, 'dollar_cost', e.target.value)}
                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {errors[`tier_${index}_price`] && (
                                                        <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_price`]}</p>
                                                    )}
                                                </div>
                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                        Description
                                                    </label>
                                                    <textarea
                                                        value={price.description}
                                                        onChange={(e) => handlePriceTierChange(index, 'description', e.target.value)}
                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black"
                                                        rows={2}
                                                    />
                                                    {errors[`tier_${index}_description`] && (
                                                        <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_description`]}</p>
                                                    )}
                                                </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                        Total Tickets Available
                                                    </label>
                                                        <input
                                                            type="number"
                                                            value={isNaN(price.tickets_available) ? '' : price.tickets_available}
                                                            onChange={(e) => handlePriceTierChange(index, 'tickets_available', e.target.value)}
                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                            min="0"
                                                        />
                                                    {errors[`tier_${index}_tickets`] && (
                                                        <p className="text-sm text-destructive mt-1">{errors[`tier_${index}_tickets`]}</p>
                                                    )}
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                            {ticketsAvailableByPrice[price.uuid!] !== undefined && (
                                                                <span>{originalTotalTickets[price.uuid!] - (ticketsAvailableByPrice[price.uuid!] ?? 0)} tickets sold so far</span>
                                                            )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                                </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="bursary-enabled"
                                            checked={formData.bursary_enabled}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bursary_enabled: checked }))}
                                        />
                                            <label htmlFor="bursary-enabled" className="text-sm font-medium text-foreground">
                                            Enable Bursary Requests
                                        </label>
                                    </div>

                                    {formData.bursary_enabled && (
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-foreground mb-2">
                                                    Bursary Type
                                                </label>
                                                <select
                                                    name="bursary_type"
                                                    value={formData.bursary_type}
                                                    onChange={handleChange}
                                                        className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                >
                                                    <option value="custom">Let users choose their amount</option>
                                                    <option value="fixed">Provide fixed bursary options</option>
                                                </select>
                                            </div>

                                            {formData.bursary_type === 'fixed' && (
                                                    <div className="space-y-4">
                                                        <div className="flex justify-between items-center">
                                                            <h3 className="text-xl font-semibold">Bursary Options</h3>
                                                            <Button 
                                                            onClick={addBursaryOption}
                                                                variant="outline"
                                                                className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                                        >
                                                            Add Option
                                                            </Button>
                                                    </div>
                                                    {formData.bursary_options.map((option, index) => (
                                                            <Card key={index} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                                <CardContent className="p-4">
                                                                    <div className="flex justify-between items-center mb-4">
                                                                        <h4 className="text-lg font-semibold">Option {index + 1}</h4>
                                                                {formData.bursary_options.length > 1 && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                        onClick={() => removeBursaryOption(index)}
                                                                                className="text-destructive hover:text-destructive/90"
                                                                    >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                )}
                                                            </div>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                                        Amount (CAD)
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={isNaN(Number(option.amount)) ? '' : option.amount}
                                                                        onChange={(e) => handleBursaryOptionChange(index, 'amount', e.target.value)}
                                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                        placeholder="0.00"
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                    {errors[`bursary_${index}_amount`] && (
                                                                        <p className="text-sm text-destructive mt-1">{errors[`bursary_${index}_amount`]}</p>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                            <label className="block text-sm font-medium text-foreground mb-2">
                                                                        Description
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        value={option.description}
                                                                        onChange={(e) => handleBursaryOptionChange(index, 'description', e.target.value)}
                                                                                className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                    />
                                                                    {errors[`bursary_${index}_description`] && (
                                                                        <p className="text-sm text-destructive mt-1">{errors[`bursary_${index}_description`]}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                                </CardContent>
                                                            </Card>
                                                    ))}
                                                </div>
                                            )}
                                            </div>
                                    )}
                                    </div>

                                    {/* Todo Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="waivers-enabled"
                                                checked={formData.waivers_enabled}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, waivers_enabled: checked }))}
                                            />
                                            <label htmlFor="waivers-enabled" className="text-sm font-medium text-foreground">
                                                Enable Todo/Checklist
                                            </label>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className="rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 w-5 h-5 flex items-center justify-center cursor-help text-xs font-medium transition-colors duration-200 border border-gray-300">?</div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Add items that must be completed to attend the event i.e. todo items, acknowledgements, waivers, etc.</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>

                                        {formData.waivers_enabled && (
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <h3 className="text-xl font-semibold">Todo Options</h3>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button 
                                                                variant="outline"
                                                                className="bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                                            >
                                                                Add Item
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <DropdownMenuItem onClick={() => addWaiverItem("link")}>Link</DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => addWaiverItem("checkbox")}>Checkbox</DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                                {errors.waiver_items && (
                                                    <Alert variant="destructive">
                                                        <AlertDescription>{errors.waiver_items}</AlertDescription>
                                                    </Alert>
                                                )}
                                                {formData.waiver_items.map((item, index) => (
                                                    <Card key={item.uuid} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                        <CardContent className="p-4">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <h4 className="text-lg font-semibold">{item.type === "checkbox" ? "Checkbox" : "Link"}</h4>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => removeWaiverItem(index)}
                                                                    className="text-destructive hover:text-destructive/90"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                            {item.type === "checkbox" && (
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                                            Message
                                                                        </label>
                                                                        <Input
                                                                            type="text"
                                                                            value={item.message}
                                                                            onChange={(e) => handleWaiverItemChange(index, 'message', e.target.value)}
                                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                            placeholder="Enter a message to display with the item"
                                                                        />
                                                                        {errors[`waiver_${index}_message`] && (
                                                                            <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_message`]}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {item.type === "link" && (
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                                            Message
                                                                        </label>
                                                                        <Input
                                                                            type="text"
                                                                            value={item.message}
                                                                            onChange={(e) => handleWaiverItemChange(index, 'message', e.target.value)}
                                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                            placeholder="Enter a message to display with the item"
                                                                        />
                                                                        {errors[`waiver_${index}_message`] && (
                                                                            <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_message`]}</p>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-2">
                                                                            Link
                                                                        </label>
                                                                        <Input
                                                                            type="url"
                                                                            value={item.link || ''}
                                                                            onChange={(e) => handleWaiverItemChange(index, 'link', e.target.value)}
                                                                            className="w-full rounded-md border-2 border-input bg-background px-4 py-2 focus:border-black h-12"
                                                                            placeholder="https://example.com"
                                                                            pattern="https?://.+"
                                                                            title="Please enter a valid URL starting with http:// or https://"
                                                                        />
                                                                        {errors[`waiver_${index}_link`] && (
                                                                            <p className="text-sm text-destructive mt-1">{errors[`waiver_${index}_link`]}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="ticket-sales"
                                            checked={formData.ticket_sales_active}
                                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ticket_sales_active: checked }))}
                                        />
                                            <label htmlFor="ticket-sales" className="text-sm font-medium text-foreground">
                                            Enable Ticket Sales
                                        </label>
                                    </div>

                                    {!formData.ticket_sales_active && (
                                            <Alert variant="destructive">
                                            <AlertDescription>
                                                Ticket sales are currently disabled. This will prevent new ticket purchases but won't affect existing tickets.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    </div>

                                    {message && (
                                        <Alert variant="destructive">
                                            <AlertDescription>{message}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                                    {showDelete && (
                                            <Button
                                                onClick={handleDeleteClick}
                                                variant="destructive"
                                                className="w-full sm:w-auto text-white transition-transform duration-200 hover:scale-105"
                                            >
                                                <Trash2 className="mr-2 h-4 w-4" />
                                                Delete Event
                                            </Button>
                                    )}
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <Button
                                                onClick={handleUpdate}
                                                className="w-full sm:w-auto bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                        >
                                                Save Changes
                                            </Button>
                                            <Button
                                                onClick={handleCancel}
                                                variant="outline"
                                                className="w-full sm:w-auto transition-transform duration-200 hover:scale-105"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>

                                    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Delete Event</DialogTitle>
                                                <DialogDescription>
                                                    Are you sure you want to delete this event? This action cannot be undone and will invalidate all existing tickets.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <DialogFooter>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setShowDeleteDialog(false)}
                                                    disabled={isDeleting}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    onClick={handleDeleteConfirm}
                                                    disabled={isDeleting}
                                                    className={`text-white ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isDeleting ? (
                                                        <>
                                                            <span className="animate-spin mr-2">⏳</span>
                                                            Deleting...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Trash2 className="mr-2 h-4 w-4" />
                                                            Delete Event
                                                        </>
                                                    )}
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-6">
                                {event.image && (
                                    <Card className="overflow-hidden">
                                        <img
                                            src={event.image}
                                            alt={event.title}
                                            className="w-full h-64 object-cover"
                                        />
                                    </Card>
                                )}

                                <Card>
                                    <CardContent className="p-6">
                                        <div className="space-y-6">
                                            <div className="space-y-2">
                                                <h3 className="text-2xl font-semibold">Description</h3>
                                                <ScrollableDescription className="text-muted-foreground">
                                                    {event.description}
                                                </ScrollableDescription>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Date</p>
                                                            <p className="text-muted-foreground truncate">{formatDate(event.date)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Time</p>
                                                            <p className="text-muted-foreground truncate">{event.time_start} - {event.time_end}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Location</p>
                                                            <p className="text-muted-foreground truncate">{event.location}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Contact Email</p>
                                                            <p className="text-muted-foreground truncate">{event.contact_email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Phone className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Contact Number</p>
                                                            <p className="text-muted-foreground truncate">{event.contact_number}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Ticket className="h-5 w-5 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">Tickets Available</p>
                                                            <Badge variant="secondary" className="shrink-0">{ticketsAvailable}</Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {prices && prices.length > 0 && (
                                                <div className="space-y-4">
                                                    <h3 className="text-2xl font-semibold">Ticket Tiers</h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {prices.map((price) => (
                                                            <Card key={price.uuid} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                                <CardContent className="p-4">
                                                                    <div className="space-y-2">
                                                                        <div className="flex justify-between items-start">
                                                                            <div className="min-w-0">
                                                                                <h4 className="font-semibold truncate">{price.nickname}</h4>
                                                                                <p className="text-sm text-muted-foreground truncate">{price.description}</p>
                                                                            </div>
                                                                            <Badge variant="secondary" className="shrink-0 ml-2">
                                                                                ${price.dollar_cost}
                                                                            </Badge>
                                                                        </div>
                                                                        <div className="flex items-center justify-between text-sm">
                                                                            <span className="text-muted-foreground truncate">
                                                                                {ticketsAvailableByPrice[price.uuid!] !== undefined && (
                                                                                    <span>{originalTotalTickets[price.uuid!] - (ticketsAvailableByPrice[price.uuid!] ?? 0)} sold</span>
                                                                                )}
                                                                            </span>
                                                                            <span className="font-medium shrink-0 ml-2">
                                                                                {price.tickets_available} total tickets
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </CardContent>
                                                            </Card>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {event.bursary_enabled && (
                                                <div className="space-y-4">
                                                    <h3 className="text-2xl font-semibold">Bursary Options</h3>
                                                    {event.bursary_type === 'custom' ? (
                                                        <p className="text-muted-foreground">
                                                            Users can request custom bursary amounts for this event.
                                                        </p>
                                                    ) : (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            {event.bursary_options?.map((option, index) => (
                                                                <Card key={index} className="bg-muted/50 hover:shadow-md transition-shadow">
                                                                    <CardContent className="p-4">
                                                                        <div className="space-y-2">
                                                                            <div className="flex justify-between items-start">
                                                                                <p className="text-sm text-muted-foreground truncate">{option.description}</p>
                                                                                <Badge variant="secondary" className="shrink-0 ml-2">
                                                                                    ${option.amount}
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    </CardContent>
                                                                </Card>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                    {!event.ticket_sales_active && (
                                                <Alert variant="destructive">
                                            <AlertDescription>
                                                Ticket sales are currently disabled for this event.
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                            <div className="flex flex-col xl:flex-row gap-4">
                                                <div className="flex flex-col sm:flex-row gap-4 order-1 xl:order-2 w-full">
                                                    <Button
                                                        asChild
                                                        variant="outline"
                                                        className="w-full transition-transform duration-200 hover:scale-105"
                                                    >
                                                        <Link to={getTenantPath(`/event/${uuid}/attendees`)}>
                                                            <Users className="mr-2 h-4 w-4" />
                                                            View Attendees
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        asChild
                                                        variant="outline"
                                                        className="w-full transition-transform duration-200 hover:scale-105"
                                                    >
                                                        <Link to={getTenantPath(`/event/${uuid}/notify-attendees`)}>
                                                            <Bell className="mr-2 h-4 w-4" />
                                                            Notify Attendees
                                                        </Link>
                                                    </Button>
                                                </div>
                                                <Button
                                                    onClick={handleCheckout}
                                                    className="w-full xl:w-auto transition-transform duration-200 hover:scale-105 order-2 xl:order-1"
                                                >
                                                    <ExternalLink className="mr-2 h-4 w-4" />
                                                    View Checkout Page
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ViewEvent;